const JOB_ID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when `s` is a non-empty UUID string (trimmed). */
export function isFieldUuidString(s: string): boolean {
    return JOB_ID_UUID_RE.test(s.trim());
}

/**
 * Parse `#field?job=<uuid>` or `#/field?job=` (fragment only). Returns the job id when it is a valid UUID;
 * otherwise null (wrong tab, missing query, or invalid value).
 */
export function parseFieldJobIdFromHashString(hash: string): string | null {
    const raw = hash.replace(/^#/, "").trim().replace(/^\//, "");
    const head = raw.split(/[?#]/)[0] ?? "";
    if (head !== "field") return null;
    const qIndex = raw.indexOf("?");
    if (qIndex < 0) return null;
    const job = new URLSearchParams(raw.slice(qIndex + 1)).get("job")?.trim() ?? "";
    return isFieldUuidString(job) ? job : null;
}

/** Hash fragment for the Field tab: `#field` or `#field?job=<uuid>` when `jobId` is valid. */
export function buildFieldTabHashJob(jobId: string | null | undefined): string {
    const id = jobId?.trim() ?? "";
    if (id && isFieldUuidString(id)) return `#field?job=${encodeURIComponent(id)}`;
    return "#field";
}

/** `scrollIntoView` behavior: `auto` when `prefers-reduced-motion: reduce`, else `smooth`. */
export function fieldPanelReducedMotionScrollBehavior(): ScrollBehavior {
    if (typeof matchMedia === "undefined") return "smooth";
    return matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

/**
 * Copy plain text to the clipboard (secure context `navigator.clipboard`, else `execCommand` fallback).
 * Returns `false` when `text` is blank or all APIs fail.
 */
export async function copyPlainTextToClipboard(text: string): Promise<boolean> {
    const t = text.trim();
    if (!t) return false;
    try {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(t);
            return true;
        }
    } catch {
        /* fall through */
    }
    try {
        if (typeof document === "undefined") return false;
        const ta = document.createElement("textarea");
        ta.value = t;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch {
        return false;
    }
}

/** Best-effort message from JSON `{ message }` or short plain-text bodies. */
export function readApiErrorMessage(body: unknown): string | null {
    if (body && typeof body === "object" && "message" in body) {
        const m = (body as { message?: unknown }).message;
        if (typeof m === "string" && m.trim()) return m.trim();
    }
    if (typeof body === "string") {
        const t = body.trim();
        if (t && t.length < 500) return t;
    }
    return null;
}

export type FieldHttpErrorContext = "jobs list" | "job detail" | "registry site" | "registry asset";

/** Subset of job header fields used for A0 registry display on Field job detail. */
export type FieldRegistryJobFields = {
    site_id?: string | null;
    asset_id?: string | null;
    registry_site_name?: string | null;
    registry_asset_name?: string | null;
};

/**
 * Single line for registry site: name and/or UUID from `GET /api/jobs/:jobId` job row.
 */
export function formatRegistrySiteDisplay(job: FieldRegistryJobFields): string {
    const id = job.site_id?.trim() ?? "";
    const name = job.registry_site_name?.trim() ?? "";
    if (!id && !name) return "—";
    if (name && id) return `${name} · ${id}`;
    return name || id;
}

/**
 * Single line for registry asset: name and/or UUID from job row.
 */
export function formatRegistryAssetDisplay(job: FieldRegistryJobFields): string {
    const id = job.asset_id?.trim() ?? "";
    const name = job.registry_asset_name?.trim() ?? "";
    if (!id && !name) return "—";
    if (name && id) return `${name} · ${id}`;
    return name || id;
}

/** Human-readable line for failed Field panel HTTP responses. */
export function formatFieldHttpError(
    status: number,
    statusText: string,
    body: unknown,
    context: FieldHttpErrorContext,
): string {
    const msg = readApiErrorMessage(body);
    const verb =
        context === "jobs list"
            ? "load the job list"
            : context === "job detail"
              ? "load this job"
              : context === "registry site"
                ? "load this registry site"
                : "load this registry asset";
    if (status === 401) return msg ?? "Sign in again — your session may have expired.";
    if (status === 403) return msg ?? `You are not allowed to ${verb}.`;
    if (status === 404 && context === "job detail") return msg ?? "That job was not found or you cannot access it.";
    if (status === 404 && context === "registry site") {
        return msg ?? "That registry site was not found or you cannot access it.";
    }
    if (status === 404 && context === "registry asset") {
        return msg ?? "That registry asset was not found or you cannot access it.";
    }
    if (msg) return `${msg} (HTTP ${status}).`;
    return `Could not ${verb} (HTTP ${status}${statusText ? ` ${statusText}` : ""}).`;
}

/**
 * Build `GET /api/jobs/:jobId` path. Returns `null` when `jobId` is not a valid UUID after trim
 * (matches server `:jobId` validation).
 */
export function buildFieldJobDetailPath(jobId: string): string | null {
    const id = jobId.trim();
    if (!JOB_ID_UUID_RE.test(id)) return null;
    return `/api/jobs/${encodeURIComponent(id)}`;
}

/** `GET /api/sites/:siteId` — same UUID validation as the server. */
export function buildFieldRegistrySitePath(siteId: string): string | null {
    const id = siteId.trim();
    if (!JOB_ID_UUID_RE.test(id)) return null;
    return `/api/sites/${encodeURIComponent(id)}`;
}

/** `GET /api/assets/:assetId` — same UUID validation as the server. */
export function buildFieldRegistryAssetPath(assetId: string): string | null {
    const id = assetId.trim();
    if (!JOB_ID_UUID_RE.test(id)) return null;
    return `/api/assets/${encodeURIComponent(id)}`;
}

/** Inputs for `GET /api/jobs` from the Field panel (paging + optional filters). */
export type FieldJobsListParams = {
    limit: number;
    offset: number;
    /** Substring filter (`q` query); trimmed; omitted when empty. */
    titleSearch?: string;
    /** Exact status match (`status` query); trimmed; omitted when empty. */
    statusExact?: string;
};

/**
 * Build `/api/jobs?limit=&offset=` with optional `q` and `status`, matching server list semantics.
 * Clamps `limit` to 1–100 and `offset` to ≥ 0 (same as `GET /api/jobs` defaults/caps).
 */
export function buildFieldJobsListPath(params: FieldJobsListParams): string {
    const limit = Math.min(100, Math.max(1, Number.isFinite(params.limit) ? Math.floor(params.limit) : 15));
    const offset = Math.max(0, Number.isFinite(params.offset) ? Math.floor(params.offset) : 0);
    let path = `/api/jobs?limit=${limit}&offset=${offset}`;
    const q = params.titleSearch?.trim() ?? "";
    if (q) path += `&q=${encodeURIComponent(q)}`;
    const st = params.statusExact?.trim() ?? "";
    if (st) path += `&status=${encodeURIComponent(st)}`;
    return path;
}

/** `sessionStorage` key for Field jobs toolbar (Phase 9 F9.1). */
export const FIELD_JOBS_FILTERS_STORAGE_KEY = "backflow:field-jobs-filters";

export type FieldJobsSavedFilters = {
    titleSearch: string;
    statusExact: string;
    limit: string;
};

function clampLimitString(s: string): string {
    const n = parseInt(s, 10);
    if (!Number.isFinite(n)) return "15";
    return String(Math.min(100, Math.max(1, n)));
}

/** Parse saved JSON; returns `null` on invalid / missing data. */
export function parseFieldJobsSavedFilters(raw: string | null): FieldJobsSavedFilters | null {
    if (raw == null || raw === "") return null;
    try {
        const o = JSON.parse(raw) as unknown;
        if (typeof o !== "object" || o === null) return null;
        const r = o as Record<string, unknown>;
        return {
            titleSearch: typeof r.titleSearch === "string" ? r.titleSearch : "",
            statusExact: typeof r.statusExact === "string" ? r.statusExact : "",
            limit: clampLimitString(typeof r.limit === "string" ? r.limit : "15"),
        };
    } catch {
        return null;
    }
}

export function serializeFieldJobsSavedFilters(f: FieldJobsSavedFilters): string {
    return JSON.stringify({
        titleSearch: f.titleSearch,
        statusExact: f.statusExact,
        limit: clampLimitString(f.limit),
    });
}
